import { create } from 'zustand'
import type { ID } from '@/types'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: ID
  title: string
  description?: string
  variant: ToastVariant
  duration?: number
}

interface ToastState {
  toasts: Toast[]
  push: (t: Omit<Toast, 'id'>) => void
  dismiss: (id: ID) => void
}

let counter = 0

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    const id = `toast-${++counter}`
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }))
    const duration = t.duration ?? 4000
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }))
    }, duration)
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
}))

/** Imperative helper usable outside React (e.g. api error handlers). */
export const toast = {
  // Confirmations disappear quickly (2s); errors/warnings linger so they can be read.
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'success', duration: 2000 }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'error', duration: 5000 }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'info', duration: 2000 }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'warning', duration: 4000 }),
}
